/**
 * Classify a player’s reported viewport (`screenWidthPx` × `screenHeightPx`) into common aspect buckets.
 * Uses long/short side ratio so orientation is explicit in the label (landscape vs portrait).
 *
 * When `TScreenRotation` is `90` or `270`, width and height are swapped before classification so the
 * bucket reflects the logical frame after the player’s display rotation (matches on-wall / content aspect).
 */

import type { TScreenRotation } from '@lucimark/shared/models/screen-rotation'

/** Relative tolerance when matching canonical aspect ratios (e.g. browser chrome slightly changes inner size). */
const RATIO_TOLERANCE = 0.028

function ratioClose(actual: number, target: number): boolean {
  if (!Number.isFinite(actual) || !Number.isFinite(target) || target <= 0) {
    return false
  }
  return Math.abs(actual - target) / target <= RATIO_TOLERANCE
}

function normalizeRotationForAspect(rotation: TScreenRotation | null | undefined): TScreenRotation {
  if (rotation === '0' || rotation === '90' || rotation === '180' || rotation === '270') {
    return rotation
  }
  return '0'
}

/**
 * Dimensions used for aspect ratio: at 90° or 270° display rotation, swap width/height so landscape/portrait
 * buckets match the rotated content frame. 0° and 180° keep reported width/height order.
 */
export function effectiveViewportDimensionsForAspect(
  widthPx: number,
  heightPx: number,
  screenRotation?: TScreenRotation | null
): { width: number, height: number } {
  const w = Math.round(widthPx)
  const h = Math.round(heightPx)
  const r = normalizeRotationForAspect(screenRotation ?? undefined)
  if (r === '90' || r === '270') {
    return { width: h, height: w }
  }
  return { width: w, height: h }
}

export type TPlayerScreenAspectClass
  = | 'square'
    | 'landscape_16_9'
    | 'landscape_4_3'
    | 'landscape_16_10'
    | 'landscape_21_9'
    | 'landscape_other'
    | 'portrait_9_16'
    | 'portrait_3_4'
    | 'portrait_10_16'
    | 'portrait_9_21'
    | 'portrait_other'
    | 'unknown'

/**
 * @param widthPx – Last-reported inner width (CSS px), e.g. from playback heartbeat.
 * @param heightPx – Last-reported inner height (CSS px).
 * @param screenRotation – Last-reported player display rotation; 90 and 270 swap axes for aspect (see module doc).
 */
export function classifyPlayerScreenAspectRatio(
  widthPx: number | null | undefined,
  heightPx: number | null | undefined,
  screenRotation?: TScreenRotation | null
): TPlayerScreenAspectClass {
  if (widthPx == null || heightPx == null) {
    return 'unknown'
  }
  const w0 = Math.round(widthPx)
  const h0 = Math.round(heightPx)
  if (w0 <= 0 || h0 <= 0) {
    return 'unknown'
  }
  const { width: w, height: h } = effectiveViewportDimensionsForAspect(w0, h0, screenRotation)

  const long = Math.max(w, h)
  const short = Math.min(w, h)
  const aspect = long / short

  if (ratioClose(aspect, 1)) {
    return 'square'
  }

  const landscape = w >= h

  if (landscape) {
    if (ratioClose(aspect, 16 / 9)) {
      return 'landscape_16_9'
    }
    if (ratioClose(aspect, 4 / 3)) {
      return 'landscape_4_3'
    }
    if (ratioClose(aspect, 16 / 10)) {
      return 'landscape_16_10'
    }
    if (ratioClose(aspect, 21 / 9)) {
      return 'landscape_21_9'
    }
    return 'landscape_other'
  }

  if (ratioClose(aspect, 16 / 9)) {
    return 'portrait_9_16'
  }
  if (ratioClose(aspect, 4 / 3)) {
    return 'portrait_3_4'
  }
  if (ratioClose(aspect, 16 / 10)) {
    return 'portrait_10_16'
  }
  if (ratioClose(aspect, 21 / 9)) {
    return 'portrait_9_21'
  }
  return 'portrait_other'
}

function gcd(a: number, b: number): number {
  let x = Math.abs(Math.round(a))
  let y = Math.abs(Math.round(b))
  while (y !== 0) {
    const t = y
    y = x % y
    x = t
  }
  return x || 1
}

/**
 * Human-readable simplified ratio like `16∶9` (unicode ratio sign).
 * When `screenRotation` is 90 or 270, uses effectiveViewportDimensionsForAspect first.
 */
export function simplifyScreenPixelRatio(
  widthPx: number,
  heightPx: number,
  screenRotation?: TScreenRotation | null
): string {
  const w0 = Math.round(widthPx)
  const h0 = Math.round(heightPx)
  if (w0 <= 0 || h0 <= 0) {
    return ''
  }
  const { width: w, height: h } = effectiveViewportDimensionsForAspect(w0, h0, screenRotation)
  const g = gcd(w, h)
  return `${Math.round(w / g)}∶${Math.round(h / g)}`
}
